import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AdminLayout from "../components/AdminLayout";
import ClassificationEventsTable from "../components/ClassificationEventsTable";
import UserFormModal from "../components/admin/UserFormModal";
import { StatusBadge } from "../components/ui/StatusBadge";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { api, extractError } from "../lib/api";
import {
  CheckCircle,
  XCircle,
  ArrowCounterClockwise,
  Trash,
  MagnifyingGlass,
  Users,
  ClockCountdown,
  ShieldCheck,
  ClockCounterClockwise,
  X,
  UserPlus,
  PencilSimple,
  PaperPlaneTilt,
  Crown,
} from "@phosphor-icons/react";

const STATUS_FILTERS = [
  { key: "all", label: "Alle" },
  { key: "ausstehend", label: "Ausstehend" },
  { key: "aktiv", label: "Aktiv" },
  { key: "abgelehnt", label: "Abgelehnt" },
];

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-AT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso) {
  if (!iso) return "Noch nie";
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return "Gerade eben";
    const min = Math.floor(sec / 60);
    if (min < 60) return `Vor ${min} ${min === 1 ? "Minute" : "Minuten"}`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Vor ${hr} ${hr === 1 ? "Stunde" : "Stunden"}`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `Vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
    const months = Math.floor(days / 30);
    if (months < 12) return `Vor ${months} ${months === 1 ? "Monat" : "Monaten"}`;
    const years = Math.floor(months / 12);
    return `Vor ${years} ${years === 1 ? "Jahr" : "Jahren"}`;
  } catch {
    return "—";
  }
}

const SIZE_LABELS = {
  starter: "MINI",
  growth: "MIDI",
  enterprise: "MAXI",
};

function RegistrationDataPanel({ u }) {
  const sizeLabel = SIZE_LABELS[u.company_size] || u.company_size || "—";
  const addressLine =
    [u.address_street, [u.address_postal_code, u.address_city].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ") || "—";
  const billingLine = u.billing_address_different
    ? (u.billing_email || "—")
    : "Identisch mit Login-E-Mail";

  const fields = [
    { label: "Vorname", value: u.first_name || "—" },
    { label: "Nachname", value: u.last_name || "—" },
    { label: "E-Mail", value: u.email },
    { label: "Firma", value: u.company || "—" },
    { label: "Paket", value: sizeLabel },
    { label: "Adresse", value: addressLine, full: true },
    { label: "Rechnungs-E-Mail", value: billingLine, full: true },
  ];

  return (
    <section
      className="border border-ink-200 bg-white rounded-sm overflow-hidden mb-6"
      data-testid="user-registration-panel"
    >
      <header className="px-5 py-3 border-b border-ink-200 bg-warm-50">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-600">
          Registrierungs-Daten
        </div>
      </header>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 p-5">
        {fields.map((f) => (
          <div
            key={f.label}
            className={f.full ? "sm:col-span-2" : undefined}
          >
            <dt className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
              {f.label}
            </dt>
            <dd
              className="text-sm text-ink-900 leading-snug break-words"
              data-testid={`user-reg-${f.label.toLowerCase().replace(/\W+/g, "-")}`}
            >
              {f.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const isCallerSuperadmin = (user?.role || "") === "superadmin";
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ ausstehend: 0, aktiv: 0, abgelehnt: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [activityUser, setActivityUser] = useState(null);
  // Iter 19.25: admin create/edit modal + resend-invite
  const [modalMode, setModalMode] = useState(null); // null | 'create' | 'edit'
  const [editTarget, setEditTarget] = useState(null);

  // Close modal on Esc
  useEffect(() => {
    if (!activityUser) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setActivityUser(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activityUser]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/stats"),
      ]);
      setUsers(usersRes.data.users || []);
      setStats(statsRes.data || { ausstehend: 0, aktiv: 0, abgelehnt: 0, total: 0 });
    } catch (err) {
      toast.error(extractError(err, "Benutzer konnten nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter !== "all" && u.status !== filter) return false;
      if (!q) return true;
      return (
        u.email.toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.company || "").toLowerCase().includes(q)
      );
    });
  }, [users, filter, search]);

  const performAction = async (id, action, label, successMsg) => {
    setBusyId(id);
    try {
      await api.post(`/admin/users/${id}/${action}`);
      toast.success(successMsg);
      await loadAll();
    } catch (err) {
      toast.error(extractError(err, `${label} fehlgeschlagen.`));
    } finally {
      setBusyId(null);
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Diesen Benutzer wirklich endgültig entfernen?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Benutzer entfernt.");
      await loadAll();
    } catch (err) {
      toast.error(extractError(err, "Löschen fehlgeschlagen."));
    } finally {
      setBusyId(null);
    }
  };

  const resendInvite = async (u) => {
    setBusyId(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
      if (data.mail_sent) {
        toast.success(`Setup-Mail erneut an ${u.email} versendet (4 Tage gültig).`);
      } else {
        toast.warning(
          `Setup-Mail-Versand fehlgeschlagen: ${data.mail_info || "unbekannter Fehler"}.`,
        );
      }
    } catch (err) {
      toast.error(extractError(err, "Setup-Mail konnte nicht erneut versendet werden."));
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = () => {
    setEditTarget(null);
    setModalMode("create");
  };

  const openEdit = (u) => {
    setEditTarget(u);
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditTarget(null);
  };

  const onSaved = () => {
    loadAll();
  };

  return (
    <AdminLayout
      title={
        <>
          Benutzer&shy;verwaltung<span className="text-brand-400">.</span>
        </>
      }
      subtitle="Schalten Sie neue Konten frei, lehnen Sie Anfragen ab oder widerrufen Sie bestehende Berechtigungen. Aktionen wirken sofort."
    >
      <Toaster position="top-right" richColors closeButton />
      <div data-testid="admin-page" data-route="users">
        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-ink-200 bg-white rounded-sm mb-8 lg:divide-x lg:divide-ink-200 divide-y lg:divide-y-0 sm:divide-y-0">
          {[
            { label: "Gesamt", value: stats.total, icon: Users, accent: "text-ink-900" },
            { label: "Ausstehend", value: stats.ausstehend, icon: ClockCountdown, accent: "text-brand-500" },
            { label: "Aktiv", value: stats.aktiv, icon: ShieldCheck, accent: "text-status-aktiv-text" },
            { label: "Abgelehnt", value: stats.abgelehnt, icon: XCircle, accent: "text-status-abgelehnt-text" },
          ].map(({ label, value, icon: Icon, accent }) => (
            <div key={label} className="p-6 relative">
              {label === "Ausstehend" && (
                <span className="absolute top-0 left-0 h-0.5 w-full bg-brand-400" />
              )}
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                  {label}
                </div>
                <Icon size={16} weight="duotone" className={accent} />
              </div>
              <div
                className="font-cabinet text-4xl font-medium text-ink-900 leading-none"
                data-testid={`admin-stat-${label.toLowerCase()}`}
              >
                {value}
              </div>
            </div>
          ))}
        </section>

        {/* Filter & Search */}
        <section className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-1 border border-ink-200 bg-white rounded-sm p-1 w-fit">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`admin-filter-${f.key}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-crisp ${
                  filter === f.key
                    ? "bg-ink-900 text-white"
                    : "text-warm-600 hover:bg-ink-100 hover:text-ink-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="relative flex-1 lg:flex-none lg:w-80">
              <MagnifyingGlass
                size={14}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-500 pointer-events-none"
              />
              <Input
                placeholder="Suche nach Name, E-Mail, Firma…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="admin-search-input"
              />
            </div>
            <Button
              variant="brand"
              size="md"
              onClick={openCreate}
              data-testid="admin-create-user-btn"
              className="shrink-0"
            >
              <UserPlus size={14} weight="bold" />
              Benutzer anlegen
            </Button>
          </div>
        </section>

        {/* Table */}
        <section className="border border-ink-200 bg-white rounded-sm overflow-hidden">
          {loading && <div className="loading-bar h-0.5" />}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="border-b border-ink-200 bg-warm-50">
                  {["Benutzer", "Firma", "Status", "Rolle", "Registriert", "Zuletzt aktiv", "Aktionen"].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 text-[10px] uppercase tracking-widest font-semibold text-warm-600"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-sm text-warm-600">
                      Keine Benutzer gefunden.
                    </td>
                  </tr>
                )}
                {filtered.map((u) => {
                  const isAdmin = u.role === "admin";
                  const isSuperadmin = u.role === "superadmin";
                  const isElevated = isAdmin || isSuperadmin;
                  const isSelf = u.id === user?.id;
                  const isBusy = busyId === u.id;
                  // Iter 19.63.15 — Edit-Button-Zugriffsregel:
                  // Admins (non-superadmin) dürfen nur 'user'-Konten editieren.
                  // Self-Editing geht weiterhin via /profil, nicht hier.
                  const callerIsSuperadmin = user?.role === "superadmin";
                  const canEdit = callerIsSuperadmin || (!isElevated);
                  return (
                    <tr
                      key={u.id}
                      className="border-b border-ink-100 last:border-b-0 transition-crisp hover:bg-ink-50"
                      data-testid={`admin-user-row-${u.email}`}
                    >
                      <td className="px-5 py-4">
                        <div className="font-medium text-ink-900">{u.name}</div>
                        <div className="text-xs text-warm-600">{u.email}</div>
                      </td>
                      <td className="px-5 py-4 text-warm-700 text-sm">
                        <div>{u.company || "—"}</div>
                        {/* Iter 19.68.17 — Company-Zuordnung als Sub-Zeile */}
                        {u.company_name && (
                          <div
                            className="text-[11px] mt-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-brand-50 border border-brand-200 text-brand-700"
                            data-testid={`admin-user-company-chip-${u.email}`}
                            title="Zugeordnetes Unternehmen (mit Monatskontingent)"
                          >
                            {u.company_name}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        {u.password_must_change ? (
                          <span
                            data-testid={`admin-user-pwsetup-${u.email}`}
                            className="inline-flex items-center rounded-sm border px-2.5 py-0.5 text-xs font-medium tracking-wide bg-status-ausstehend-bg text-status-ausstehend-text border-status-ausstehend-border"
                            title="Konto freigeschaltet — der Benutzer hat das Setup-Passwort per E-Mail erhalten und muss es noch selbst vergeben. Bis dahin ist kein Login möglich."
                          >
                            <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-status-ausstehend-border animate-pulse" />
                            Passwort ausstehend
                          </span>
                        ) : (
                          <StatusBadge status={u.status} />
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          data-testid={`admin-user-role-${u.email}`}
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium border ${
                            isSuperadmin
                              ? "border-ink-900 bg-ink-900 text-brand-400"
                              : isAdmin
                                ? "border-brand-500 bg-brand-400 text-ink-900"
                                : "border-ink-300 text-warm-700"
                          }`}
                          title={
                            isSuperadmin
                              ? "Superadmin — kann KVs bearbeiten und weitere Superadmins ernennen."
                              : isAdmin
                                ? "Admin — kann Benutzer verwalten, KVs nur ansehen."
                                : "Standard-Benutzer"
                          }
                        >
                          {isSuperadmin ? <Crown size={12} weight="fill" /> : null}
                          {isAdmin ? <ShieldCheck size={12} weight="bold" /> : null}
                          {isSuperadmin ? "Superadmin" : isAdmin ? "Admin" : "Benutzer"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs text-warm-500">
                        {formatDate(u.created_at)}
                      </td>
                      <td
                        className="px-5 py-4 text-xs"
                        data-testid={`admin-user-last-login-${u.email}`}
                      >
                        <button
                          type="button"
                          onClick={() => setActivityUser(u)}
                          data-testid={`admin-user-activity-btn-${u.email}`}
                          className="group text-left hover:underline focus:outline-none focus-visible:ring-1 focus-visible:ring-brand-400 rounded-sm px-1 -mx-1"
                          title="Aktivitäten dieses Benutzers anzeigen"
                        >
                          <div
                            className={
                              u.last_login_at
                                ? "text-ink-900 font-medium group-hover:text-brand-600"
                                : "text-warm-400 italic group-hover:text-brand-600"
                            }
                          >
                            {formatRelative(u.last_login_at)}
                          </div>
                          {u.last_login_at && (
                            <div className="text-[10px] text-warm-500 mt-0.5">
                              {formatDate(u.last_login_at)}
                            </div>
                          )}
                          <div className="text-[10px] text-brand-600 mt-1 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-crisp">
                            <ClockCounterClockwise size={10} weight="bold" />
                            Aktivitäten anzeigen
                          </div>
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        {isSelf ? (
                          <span className="text-xs text-warm-400">—</span>
                        ) : isSuperadmin && !isCallerSuperadmin ? (
                          <span
                            data-testid={`admin-user-readonly-${u.email}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-wider border border-ink-200 text-warm-600"
                            title="Nur Superadmins können Superadmin-Konten bearbeiten."
                          >
                            Schreibgeschützt
                          </span>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            {u.password_must_change && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => resendInvite(u)}
                                disabled={isBusy}
                                data-testid={`admin-resend-invite-btn-${u.email}`}
                                title="Setup-Mail erneut senden (4 Tage gültig)"
                              >
                                <PaperPlaneTilt size={14} weight="bold" />
                                Setup-Mail
                              </Button>
                            )}
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openEdit(u)}
                              disabled={isBusy || !canEdit}
                              data-testid={`admin-edit-btn-${u.email}`}
                              title={
                                !canEdit
                                  ? "Admins und Superadmins können nur von Superadmins bearbeitet werden."
                                  : "Stammdaten bearbeiten"
                              }
                            >
                              <PencilSimple size={14} weight="bold" />
                              Bearbeiten
                            </Button>
                            {!isElevated && u.status !== "aktiv" && (
                              <Button
                                variant="success"
                                size="sm"
                                onClick={() =>
                                  performAction(
                                    u.id,
                                    "approve",
                                    "Freischalten",
                                    `${u.email} wurde freigeschaltet.`
                                  )
                                }
                                disabled={isBusy}
                                data-testid={`admin-approve-btn-${u.email}`}
                              >
                                <CheckCircle size={14} weight="bold" />
                                Freischalten
                              </Button>
                            )}
                            {!isElevated && u.status !== "abgelehnt" && (
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() =>
                                  performAction(
                                    u.id,
                                    "reject",
                                    "Ablehnen",
                                    `${u.email} wurde abgelehnt.`
                                  )
                                }
                                disabled={isBusy}
                                data-testid={`admin-reject-btn-${u.email}`}
                              >
                                <XCircle size={14} weight="bold" />
                                Ablehnen
                              </Button>
                            )}
                            {!isElevated && u.status === "aktiv" && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() =>
                                  performAction(
                                    u.id,
                                    "revoke",
                                    "Widerrufen",
                                    `${u.email} wurde auf „Ausstehend" zurückgesetzt.`
                                  )
                                }
                                disabled={isBusy}
                                data-testid={`admin-revoke-btn-${u.email}`}
                              >
                                <ArrowCounterClockwise size={14} weight="bold" />
                                Widerrufen
                              </Button>
                            )}
                            {!isElevated && (
                              <button
                                onClick={() => deleteUser(u.id)}
                                disabled={isBusy}
                                data-testid={`admin-delete-btn-${u.email}`}
                                className="inline-flex items-center justify-center h-7 w-7 rounded-sm border border-ink-200 text-ink-400 hover:text-status-abgelehnt-text hover:border-status-abgelehnt-border transition-crisp disabled:opacity-50"
                                aria-label="Benutzer entfernen"
                              >
                                <Trash size={13} weight="bold" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {activityUser && (
        <div
          className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center sm:p-6 bg-ink-900/60 backdrop-blur-sm"
          onClick={() => setActivityUser(null)}
          data-testid="user-activity-modal"
        >
          <div
            className="relative bg-white border border-ink-200 rounded-sm w-full max-w-4xl flex flex-col max-h-[92vh] sm:max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 left-0 h-1 w-20 bg-brand-400" aria-hidden="true" />
            <div className="px-6 py-5 border-b border-ink-200 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500">
                  Aktivitäten von
                </div>
                <h2 className="font-cabinet text-2xl tracking-tight text-ink-900 font-medium leading-tight mt-0.5 truncate">
                  {activityUser.name || activityUser.email}
                </h2>
                <div className="text-xs text-warm-600 mt-1 truncate">
                  {activityUser.company ? `${activityUser.company} · ` : ""}
                  {activityUser.email}
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-warm-600">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-warm-500">Letzter Login:</span>
                    <strong className="text-ink-900">
                      {formatRelative(activityUser.last_login_at)}
                    </strong>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-warm-500">Registriert:</span>
                    <strong className="text-ink-900">{formatDate(activityUser.created_at)}</strong>
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActivityUser(null)}
                data-testid="user-activity-close"
                className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-sm border border-ink-200 text-warm-600 hover:bg-ink-50 hover:text-ink-900 transition-crisp"
                aria-label="Modal schließen"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 bg-warm-50">
              <RegistrationDataPanel u={activityUser} />
              <ClassificationEventsTable
                userId={activityUser.id}
                hideUserColumn
                pageSize={25}
                emptyMessage={`${activityUser.name || activityUser.email} hat noch keine Einstufung abgeschlossen.`}
                testIdPrefix="user-events"
              />
            </div>
          </div>
        </div>
      )}

      {modalMode && (
        <UserFormModal
          mode={modalMode}
          initialUser={editTarget}
          callerRole={user?.role}
          currentUserId={user?.id}
          onClose={closeModal}
          onSaved={onSaved}
        />
      )}
    </AdminLayout>
  );
}
